import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Calendar,
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  Trash2,
  User,
  Repeat,
  AlertCircle,
  Pencil,
  List,
  CalendarDays,
  ArrowUpDown,
} from 'lucide-react';

const DUTY_LABELS = {
  front_desk: 'Front Desk',
  coaching: 'Coaching',
  academy_management: 'Academy Mgmt'
};

const DUTY_COLORS = {
  front_desk: 'bg-blue-100 text-blue-700 border-blue-200',
  coaching: 'bg-green-100 text-green-700 border-green-200',
  academy_management: 'bg-purple-100 text-purple-700 border-purple-200'
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Helper function to get Monday of the week for a given date (avoiding timezone issues)
const getMondayOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  // getDay() returns 0 for Sunday, 1 for Monday, etc.
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

// Format date as YYYY-MM-DD using local date (not UTC)
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Parse a YYYY-MM-DD string as a local date
const parseDateLocal = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const ShiftScheduling = () => {
  const [schedule, setSchedule] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const monday = getMondayOfWeek(today);
    return formatDateLocal(monday);
  });
  
  // View mode: 'calendar' or 'list'
  const [viewMode, setViewMode] = useState('calendar');
  
  // List view state
  const [allShifts, setAllShifts] = useState([]);
  const [listDateRange, setListDateRange] = useState({
    start: formatDateLocal(new Date()),
    end: formatDateLocal(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)) // 30 days ahead
  });
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteMode, setBulkDeleteMode] = useState('selected'); // 'selected' or 'series'
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recurringEditDialogOpen, setRecurringEditDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [editingShift, setEditingShift] = useState(null);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [formData, setFormData] = useState({
    staff_id: '',
    start_time: '',
    end_time: '',
    duty_type: '',
    notes: ''
  });
  
  // Edit form data (separate from create form)
  const [editFormData, setEditFormData] = useState({
    staff_id: '',
    date: '',
    start_time: '',
    end_time: '',
    duty_type: '',
    notes: '',
    update_recurring: false
  });
  
  // Recurring shift form data
  const [recurringData, setRecurringData] = useState({
    staff_id: '',
    duty_type: '',
    start_time: '09:00',
    end_time: '17:00',
    recurrence_type: 'weekdays',
    specific_days: [],
    start_date: formatDateLocal(new Date()),
    end_date: '',
    notes: ''
  });
  const [creatingRecurring, setCreatingRecurring] = useState(false);

  useEffect(() => {
    if (viewMode === 'calendar') {
      fetchData();
    } else {
      fetchListData();
    }
  }, [weekStart, viewMode, listDateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scheduleRes, staffRes] = await Promise.all([
        api.get(`/admin/schedule/week?week_start=${weekStart}`),
        api.get('/staff')
      ]);
      setSchedule(scheduleRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchListData = async () => {
    setLoading(true);
    try {
      const [shiftsRes, staffRes] = await Promise.all([
        api.get(`/admin/shifts?start_date=${listDateRange.start}&end_date=${listDateRange.end}`),
        api.get('/staff')
      ]);
      setAllShifts(shiftsRes.data);
      setStaff(staffRes.data);
      setSelectedShifts([]);
    } catch (error) {
      console.error('Error fetching list data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter to only active staff for assignment
  const activeStaff = staff.filter(s => (s.profile?.status || 'active') === 'active');

  // Sorting for list view
  const sortedShifts = [...allShifts].sort((a, b) => {
    let aVal, bVal;
    switch (sortColumn) {
      case 'date':
        aVal = a.date;
        bVal = b.date;
        break;
      case 'staff':
        aVal = a.staff_name || '';
        bVal = b.staff_name || '';
        break;
      case 'time':
        aVal = a.start_time;
        bVal = b.start_time;
        break;
      case 'duty':
        aVal = a.duty_type;
        bVal = b.duty_type;
        break;
      default:
        return 0;
    }
    if (sortDirection === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    } else {
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    }
  });

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const toggleShiftSelection = (shiftId) => {
    setSelectedShifts(prev => 
      prev.includes(shiftId) 
        ? prev.filter(id => id !== shiftId)
        : [...prev, shiftId]
    );
  };

  const toggleAllShifts = () => {
    if (selectedShifts.length === sortedShifts.length) {
      setSelectedShifts([]);
    } else {
      setSelectedShifts(sortedShifts.map(s => s.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      if (bulkDeleteMode === 'selected') {
        await api.post('/admin/shifts/bulk-delete', { shift_ids: selectedShifts });
        toast.success(`Deleted ${selectedShifts.length} shifts`);
      } else {
        // Delete entire recurring series
        const recurringGroupIds = [...new Set(
          sortedShifts
            .filter(s => selectedShifts.includes(s.id) && s.recurring_group_id)
            .map(s => s.recurring_group_id)
        )];
        
        if (recurringGroupIds.length === 0) {
          toast.error('No recurring series found in selection');
          return;
        }
        
        await api.post('/admin/shifts/bulk-delete', { 
          recurring_group_ids: recurringGroupIds,
          delete_future_only: true 
        });
        toast.success(`Deleted recurring series (${recurringGroupIds.length} groups)`);
      }
      setBulkDeleteDialogOpen(false);
      setSelectedShifts([]);
      fetchListData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete shifts');
    }
  };

  const navigateWeek = (direction) => {
    const current = parseDateLocal(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(formatDateLocal(current));
  };

  const openAddShiftDialog = (date) => {
    setSelectedDate(date);
    // Set default times based on business hours
    const daySchedule = schedule?.schedule?.find(d => d.date === date);
    if (daySchedule?.business_hours) {
      setFormData(prev => ({
        ...prev,
        start_time: daySchedule.business_hours.open_time,
        end_time: daySchedule.business_hours.close_time
      }));
    }
    setDialogOpen(true);
  };

  const openEditShiftDialog = (shift, date) => {
    setEditingShift(shift);
    setEditFormData({
      staff_id: shift.staff_id,
      date: date,
      start_time: shift.start_time,
      end_time: shift.end_time,
      duty_type: shift.duty_type,
      notes: shift.notes || '',
      update_recurring: false
    });
    
    // If it's a recurring shift, show the recurring choice dialog first
    if (shift.recurring) {
      setRecurringEditDialogOpen(true);
    } else {
      setEditDialogOpen(true);
    }
  };

  const handleRecurringEditChoice = (updateAll) => {
    setEditFormData(prev => ({ ...prev, update_recurring: updateAll }));
    setRecurringEditDialogOpen(false);
    setEditDialogOpen(true);
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    if (!formData.staff_id || !formData.start_time || !formData.end_time || !formData.duty_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await api.post('/admin/shifts', {
        staff_id: formData.staff_id,
        date: selectedDate,
        start_time: formData.start_time,
        end_time: formData.end_time,
        duty_type: formData.duty_type,
        notes: formData.notes || null
      });
      toast.success('Shift created');
      setDialogOpen(false);
      setFormData({ staff_id: '', start_time: '', end_time: '', duty_type: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shift');
    }
  };

  const handleUpdateShift = async (e) => {
    e.preventDefault();
    if (!editingShift) return;
    
    if (!editFormData.staff_id || !editFormData.start_time || !editFormData.end_time || !editFormData.duty_type) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await api.put(`/admin/shifts/${editingShift.id}`, {
        staff_id: editFormData.staff_id,
        date: editFormData.date,
        start_time: editFormData.start_time,
        end_time: editFormData.end_time,
        duty_type: editFormData.duty_type,
        notes: editFormData.notes || null,
        update_recurring: editFormData.update_recurring
      });
      
      const message = editFormData.update_recurring 
        ? 'All future recurring shifts updated' 
        : 'Shift updated';
      toast.success(message);
      setEditDialogOpen(false);
      setEditingShift(null);
      setEditFormData({ staff_id: '', date: '', start_time: '', end_time: '', duty_type: '', notes: '', update_recurring: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shift');
    }
  };

  const handleDeleteShift = async () => {
    if (!shiftToDelete) return;
    try {
      await api.delete(`/admin/shifts/${shiftToDelete}`);
      toast.success('Shift deleted');
      setDeleteDialogOpen(false);
      setShiftToDelete(null);
      // Refresh the correct view based on current mode
      if (viewMode === 'calendar') {
        fetchData();
      } else {
        fetchListData();
      }
    } catch (error) {
      toast.error('Failed to delete shift');
    }
  };

  const openDeleteDialog = (shiftId) => {
    setShiftToDelete(shiftId);
    setDeleteDialogOpen(true);
  };

  // Create recurring shifts
  const handleCreateRecurringShifts = async () => {
    if (!recurringData.staff_id || !recurringData.duty_type || !recurringData.start_date || !recurringData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (recurringData.recurrence_type === 'specific_days' && recurringData.specific_days.length === 0) {
      toast.error('Please select at least one day');
      return;
    }
    
    setCreatingRecurring(true);
    try {
      const response = await api.post('/admin/shifts/recurring', recurringData);
      const { shifts_created, shifts_skipped } = response.data;
      
      if (shifts_created.length > 0) {
        toast.success(`Created ${shifts_created.length} shifts`);
      }
      if (shifts_skipped.length > 0) {
        toast.warning(`${shifts_skipped.length} shifts skipped (already exist)`);
      }
      
      setRecurringDialogOpen(false);
      setRecurringData({
        staff_id: '',
        duty_type: '',
        start_time: '09:00',
        end_time: '17:00',
        recurrence_type: 'weekdays',
        specific_days: [],
        start_date: formatDateLocal(new Date()),
        end_date: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create recurring shifts');
    } finally {
      setCreatingRecurring(false);
    }
  };

  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Get available duties for selected staff
  const getStaffDuties = (staffId) => {
    const member = staff.find(s => s.id === staffId);
    return member?.profile?.assigned_duties || [];
  };

  // Format date for display (more reliable than toLocaleDateString)
  const formatDateDisplay = (dateStr, options = {}) => {
    const date = parseDateLocal(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const fullMonths = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    if (options.weekday) {
      return `${weekdays[date.getDay()]}, ${fullMonths[date.getMonth()]} ${date.getDate()}`;
    }
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  if (loading) {
    return (
      <DashboardLayout title="Staff Scheduling">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Staff Scheduling">
      <div className="space-y-6" data-testid="shift-scheduling-page">
        {/* Header */}
        <div className="space-y-3">
          <div>
            <p className="text-slate-600">
              {schedule?.season_name || 'Schedule'} - Schedule staff shifts based on business hours
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Button 
              variant="outline"
              onClick={() => setRecurringDialogOpen(true)}
              data-testid="recurring-shifts-btn"
            >
              <Repeat className="h-4 w-4 mr-2" />
              Create Recurring Shifts
            </Button>
          </div>
        </div>

        {/* View Mode Tabs */}
        <Tabs value={viewMode} onValueChange={setViewMode} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              Calendar View
            </TabsTrigger>
            <TabsTrigger value="list" className="flex items-center gap-2">
              <List className="h-4 w-4" />
              List View
            </TabsTrigger>
          </TabsList>

          {/* Calendar View Tab */}
          <TabsContent value="calendar">
            {/* Week Navigation */}
            <div className="flex items-center justify-end gap-2 mb-4">
              <Button variant="outline" size="icon" onClick={() => navigateWeek(-1)} data-testid="prev-week">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium px-3 min-w-[140px] text-center">
                Week of {formatDateDisplay(weekStart)}
              </span>
              <Button variant="outline" size="icon" onClick={() => navigateWeek(1)} data-testid="next-week">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Weekly Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {schedule?.schedule?.map((day) => (
            <Card 
              key={day.date} 
              className={`border ${!day.business_hours?.is_open ? 'bg-slate-100 border-slate-200' : 'border-slate-200'}`}
            >
              <CardHeader className="p-3 border-b border-slate-100">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-sm">{day.day_name}</p>
                    <p className="text-xs text-slate-500">
                      {formatDateDisplay(day.date)}
                    </p>
                  </div>
                  {day.business_hours?.is_open && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => openAddShiftDialog(day.date)}
                      data-testid={`add-shift-${day.date}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {day.business_hours?.is_open ? (
                  <p className="text-xs text-slate-400 mt-1">
                    {formatTime(day.business_hours.open_time)} - {formatTime(day.business_hours.close_time)}
                  </p>
                ) : (
                  <p className="text-xs text-slate-400 mt-1">Closed</p>
                )}
              </CardHeader>
              <CardContent className="p-2 min-h-[120px]">
                {day.shifts?.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">No shifts</p>
                ) : (
                  <div className="space-y-2">
                    {day.shifts?.map((shift) => (
                      <div
                        key={shift.id}
                        className={`p-2 rounded border text-xs ${DUTY_COLORS[shift.duty_type] || 'bg-slate-100'}`}
                        data-testid={`shift-${shift.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span className="font-medium truncate max-w-[60px]">{shift.staff_name}</span>
                            {shift.recurring && (
                              <Repeat className="h-3 w-3 text-slate-400" title="Recurring shift" />
                            )}
                          </div>
                          <div className="flex gap-0.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 hover:bg-blue-100"
                              onClick={() => openEditShiftDialog(shift, day.date)}
                              data-testid={`edit-shift-${shift.id}`}
                            >
                              <Pencil className="h-3 w-3 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 hover:bg-rose-100"
                              onClick={() => openDeleteDialog(shift.id)}
                              data-testid={`delete-shift-${shift.id}`}
                            >
                              <Trash2 className="h-3 w-3 text-rose-500" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <Clock className="h-3 w-3" />
                          <span>{formatTime(shift.start_time)} - {formatTime(shift.end_time)}</span>
                        </div>
                        <span className="inline-block mt-1 px-1 rounded text-[10px] bg-white/50">
                          {DUTY_LABELS[shift.duty_type] || shift.duty_type}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
            </div>

            {/* Legend */}
            <Card className="border border-slate-200 mt-4">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4">
                  <span className="text-sm text-slate-500 font-medium">Duty Types:</span>
                  {Object.entries(DUTY_LABELS).map(([key, label]) => (
                    <span key={key} className={`px-2 py-1 rounded text-xs ${DUTY_COLORS[key]}`}>
                      {label}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* List View Tab */}
          <TabsContent value="list">
            {/* Date Range Filter */}
            <div className="flex flex-wrap items-end gap-4 mb-4">
              <div className="space-y-1">
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={listDateRange.start}
                  onChange={(e) => setListDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-40"
                  data-testid="list-start-date"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">End Date</Label>
                <Input
                  type="date"
                  value={listDateRange.end}
                  onChange={(e) => setListDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-40"
                  data-testid="list-end-date"
                />
              </div>
              {selectedShifts.length > 0 && (
                <div className="flex items-center gap-2 ml-auto">
                  <Badge variant="outline" className="text-sm">
                    {selectedShifts.length} selected
                  </Badge>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setBulkDeleteDialogOpen(true)}
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete Selected
                  </Button>
                </div>
              )}
            </div>

            {/* Shifts Table */}
            <Card className="border border-slate-200">
              <CardContent className="p-0">
                {sortedShifts.length === 0 ? (
                  <div className="p-12 text-center">
                    <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No shifts found in this date range</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <Checkbox
                              checked={selectedShifts.length === sortedShifts.length && sortedShifts.length > 0}
                              onCheckedChange={toggleAllShifts}
                              data-testid="select-all-shifts"
                            />
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => handleSort('date')}
                          >
                            <div className="flex items-center gap-1">
                              Date
                              <ArrowUpDown className="h-3 w-3" />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => handleSort('staff')}
                          >
                            <div className="flex items-center gap-1">
                              Staff Member
                              <ArrowUpDown className="h-3 w-3" />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => handleSort('time')}
                          >
                            <div className="flex items-center gap-1">
                              Time
                              <ArrowUpDown className="h-3 w-3" />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => handleSort('duty')}
                          >
                            <div className="flex items-center gap-1">
                              Duty
                              <ArrowUpDown className="h-3 w-3" />
                            </div>
                          </TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="w-24">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedShifts.map((shift) => (
                          <TableRow key={shift.id} data-testid={`list-shift-${shift.id}`}>
                            <TableCell>
                              <Checkbox
                                checked={selectedShifts.includes(shift.id)}
                                onCheckedChange={() => toggleShiftSelection(shift.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">{formatDateDisplay(shift.date, { weekday: true })}</div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-slate-400" />
                                {shift.staff_name || 'Unknown'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Clock className="h-3 w-3 text-slate-400" />
                                {formatTime(shift.start_time)} - {formatTime(shift.end_time)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className={`inline-block px-2 py-1 rounded text-xs ${DUTY_COLORS[shift.duty_type] || 'bg-slate-100'}`}>
                                {DUTY_LABELS[shift.duty_type] || shift.duty_type}
                              </span>
                            </TableCell>
                            <TableCell>
                              {shift.recurring ? (
                                <Badge variant="outline" className="text-xs">
                                  <Repeat className="h-3 w-3 mr-1" />
                                  Recurring
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">Single</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-blue-50"
                                  onClick={() => openEditShiftDialog(shift, shift.date)}
                                  data-testid={`list-edit-${shift.id}`}
                                >
                                  <Pencil className="h-4 w-4 text-blue-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-rose-50"
                                  onClick={() => openDeleteDialog(shift.id)}
                                  data-testid={`list-delete-${shift.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-rose-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary Stats */}
            {sortedShifts.length > 0 && (
              <div className="flex flex-wrap gap-4 mt-4">
                <Card className="flex-1 min-w-[150px]">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-slate-800">{sortedShifts.length}</p>
                    <p className="text-xs text-slate-500">Total Shifts</p>
                  </CardContent>
                </Card>
                <Card className="flex-1 min-w-[150px]">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {sortedShifts.filter(s => s.recurring).length}
                    </p>
                    <p className="text-xs text-slate-500">Recurring</p>
                  </CardContent>
                </Card>
                <Card className="flex-1 min-w-[150px]">
                  <CardContent className="p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {[...new Set(sortedShifts.map(s => s.staff_id))].length}
                    </p>
                    <p className="text-xs text-slate-500">Staff Members</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Bulk Delete Dialog */}
        <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-rose-500" />
                Delete {selectedShifts.length} Shifts
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-4">
                <p>Choose how to delete the selected shifts:</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="radio"
                      name="deleteMode"
                      value="selected"
                      checked={bulkDeleteMode === 'selected'}
                      onChange={() => setBulkDeleteMode('selected')}
                    />
                    <div>
                      <p className="font-medium">Delete Selected Only</p>
                      <p className="text-sm text-slate-500">Only delete the {selectedShifts.length} selected shifts</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="radio"
                      name="deleteMode"
                      value="series"
                      checked={bulkDeleteMode === 'series'}
                      onChange={() => setBulkDeleteMode('series')}
                    />
                    <div>
                      <p className="font-medium">Delete Entire Recurring Series</p>
                      <p className="text-sm text-slate-500">Delete all future shifts in each recurring group</p>
                    </div>
                  </label>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleBulkDelete}
                className="bg-rose-500 hover:bg-rose-600"
                data-testid="confirm-bulk-delete"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Add Shift Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                Add Shift - {selectedDate && formatDateDisplay(selectedDate, { weekday: true })}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateShift} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Staff Member * <span className="text-xs text-slate-400">(Active only)</span></Label>
                <Select
                  value={formData.staff_id}
                  onValueChange={(value) => {
                    setFormData({ ...formData, staff_id: value, duty_type: '' });
                  }}
                >
                  <SelectTrigger data-testid="shift-staff-select">
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeStaff.filter(s => s.profile?.assigned_duties?.length > 0).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                    {activeStaff.filter(s => s.profile?.assigned_duties?.length > 0).length === 0 && (
                      <div className="px-2 py-3 text-sm text-slate-500">
                        No active staff with duties assigned
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {formData.staff_id && (
                <div className="space-y-2">
                  <Label>Duty Type *</Label>
                  <Select
                    value={formData.duty_type}
                    onValueChange={(value) => setFormData({ ...formData, duty_type: value })}
                  >
                    <SelectTrigger data-testid="shift-duty-select">
                      <SelectValue placeholder="Select duty" />
                    </SelectTrigger>
                    <SelectContent>
                      {getStaffDuties(formData.staff_id).map((duty) => (
                        <SelectItem key={duty} value={duty}>
                          {DUTY_LABELS[duty] || duty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                    data-testid="shift-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    required
                    data-testid="shift-end-time"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Any special instructions..."
                  rows={2}
                  data-testid="shift-notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="create-shift-btn">
                  Create Shift
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Shift Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Pencil className="h-5 w-5 text-blue-500" />
                Edit Shift
                {editFormData.update_recurring && (
                  <Badge variant="outline" className="ml-2 text-xs">Updating All Future</Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateShift} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Staff Member *</Label>
                <Select
                  value={editFormData.staff_id}
                  onValueChange={(value) => {
                    setEditFormData({ ...editFormData, staff_id: value, duty_type: '' });
                  }}
                >
                  <SelectTrigger data-testid="edit-shift-staff-select">
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeStaff.filter(s => s.profile?.assigned_duties?.length > 0).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {editFormData.staff_id && (
                <div className="space-y-2">
                  <Label>Duty Type *</Label>
                  <Select
                    value={editFormData.duty_type}
                    onValueChange={(value) => setEditFormData({ ...editFormData, duty_type: value })}
                  >
                    <SelectTrigger data-testid="edit-shift-duty-select">
                      <SelectValue placeholder="Select duty" />
                    </SelectTrigger>
                    <SelectContent>
                      {getStaffDuties(editFormData.staff_id).map((duty) => (
                        <SelectItem key={duty} value={duty}>
                          {DUTY_LABELS[duty] || duty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {!editFormData.update_recurring && (
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={editFormData.date}
                    onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                    required
                    data-testid="edit-shift-date"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <Input
                    type="time"
                    value={editFormData.start_time}
                    onChange={(e) => setEditFormData({ ...editFormData, start_time: e.target.value })}
                    required
                    data-testid="edit-shift-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <Input
                    type="time"
                    value={editFormData.end_time}
                    onChange={(e) => setEditFormData({ ...editFormData, end_time: e.target.value })}
                    required
                    data-testid="edit-shift-end-time"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  placeholder="Any special instructions..."
                  rows={2}
                  data-testid="edit-shift-notes"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-500 hover:bg-blue-600" data-testid="update-shift-btn">
                  Update Shift
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Recurring Edit Choice Dialog */}
        <AlertDialog open={recurringEditDialogOpen} onOpenChange={setRecurringEditDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Repeat className="h-5 w-5 text-blue-500" />
                Edit Recurring Shift
              </AlertDialogTitle>
              <AlertDialogDescription>
                This is part of a recurring schedule. Would you like to edit just this occurrence or all future occurrences?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col sm:flex-row gap-2">
              <AlertDialogCancel onClick={() => setRecurringEditDialogOpen(false)}>
                Cancel
              </AlertDialogCancel>
              <Button
                variant="outline"
                onClick={() => handleRecurringEditChoice(false)}
                data-testid="edit-single-occurrence"
              >
                This Day Only
              </Button>
              <Button
                className="bg-blue-500 hover:bg-blue-600"
                onClick={() => handleRecurringEditChoice(true)}
                data-testid="edit-all-future"
              >
                All Future Occurrences
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Shift</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this shift? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteShift}
                className="bg-rose-500 hover:bg-rose-600"
                data-testid="confirm-delete-shift"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Recurring Shifts Dialog */}
        <Dialog open={recurringDialogOpen} onOpenChange={setRecurringDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Repeat className="h-5 w-5 text-blue-500" />
                Create Recurring Shifts
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Staff Selection */}
              <div className="space-y-2">
                <Label>Staff Member * <span className="text-xs text-slate-400">(Active only)</span></Label>
                <Select
                  value={recurringData.staff_id}
                  onValueChange={(value) => setRecurringData({ ...recurringData, staff_id: value, duty_type: '' })}
                >
                  <SelectTrigger data-testid="recurring-staff-select">
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeStaff.filter(s => s.profile?.assigned_duties?.length > 0).map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Duty Type */}
              {recurringData.staff_id && (
                <div className="space-y-2">
                  <Label>Duty Type *</Label>
                  <Select
                    value={recurringData.duty_type}
                    onValueChange={(value) => setRecurringData({ ...recurringData, duty_type: value })}
                  >
                    <SelectTrigger data-testid="recurring-duty-select">
                      <SelectValue placeholder="Select duty" />
                    </SelectTrigger>
                    <SelectContent>
                      {getStaffDuties(recurringData.staff_id).map((duty) => (
                        <SelectItem key={duty} value={duty}>
                          {DUTY_LABELS[duty] || duty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Start Time *
                  </Label>
                  <Input
                    type="time"
                    value={recurringData.start_time}
                    onChange={(e) => setRecurringData({ ...recurringData, start_time: e.target.value })}
                    data-testid="recurring-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <Input
                    type="time"
                    value={recurringData.end_time}
                    onChange={(e) => setRecurringData({ ...recurringData, end_time: e.target.value })}
                    data-testid="recurring-end-time"
                  />
                </div>
              </div>

              {/* Recurrence Type */}
              <div className="space-y-2">
                <Label>Repeat Pattern *</Label>
                <Select
                  value={recurringData.recurrence_type}
                  onValueChange={(value) => setRecurringData({ ...recurringData, recurrence_type: value, specific_days: [] })}
                >
                  <SelectTrigger data-testid="recurring-pattern-select">
                    <SelectValue placeholder="Select pattern" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekdays">Weekdays (Mon-Fri)</SelectItem>
                    <SelectItem value="weekends">Weekends (Sat-Sun)</SelectItem>
                    <SelectItem value="daily">Daily (All days)</SelectItem>
                    <SelectItem value="specific_days">Specific Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Specific Days Selection */}
              {recurringData.recurrence_type === 'specific_days' && (
                <div className="space-y-2">
                  <Label>Select Days *</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => {
                          const newDays = recurringData.specific_days.includes(day)
                            ? recurringData.specific_days.filter(d => d !== day)
                            : [...recurringData.specific_days, day];
                          setRecurringData({ ...recurringData, specific_days: newDays });
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          recurringData.specific_days.includes(day)
                            ? 'bg-blue-500 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`recurring-day-${day.toLowerCase()}`}
                      >
                        {day.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Date Range */}
              <div className="p-3 bg-blue-50 rounded-lg space-y-3">
                <p className="text-sm font-medium text-blue-700 flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Date Range
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Start Date *</Label>
                    <Input
                      type="date"
                      value={recurringData.start_date}
                      onChange={(e) => setRecurringData({ ...recurringData, start_date: e.target.value })}
                      data-testid="recurring-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">End Date *</Label>
                    <Input
                      type="date"
                      value={recurringData.end_date}
                      onChange={(e) => setRecurringData({ ...recurringData, end_date: e.target.value })}
                      data-testid="recurring-end-date"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={recurringData.notes}
                  onChange={(e) => setRecurringData({ ...recurringData, notes: e.target.value })}
                  placeholder="Notes for these shifts..."
                  data-testid="recurring-notes"
                  rows={2}
                />
              </div>

              {/* Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  Existing shifts on the same date/time will be skipped.
                </p>
              </div>

              <DialogFooter className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setRecurringDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={handleCreateRecurringShifts}
                  disabled={creatingRecurring || !recurringData.staff_id || !recurringData.duty_type || !recurringData.start_date || !recurringData.end_date}
                  data-testid="create-recurring-btn"
                >
                  {creatingRecurring ? (
                    <span className="animate-spin mr-1">⏳</span>
                  ) : (
                    <Repeat className="h-4 w-4 mr-1" />
                  )}
                  Create Shifts
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ShiftScheduling;
